import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { buildModel } from "../geometry";

const DEG = Math.PI / 180;
const V3 = (x, y, z) => new THREE.Vector3(x, y, z);
const css = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888888";
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const fmtMm = (m) => (Math.round((m * 1000) / 50) * 50).toLocaleString("en-IN");

/** Imperative three.js stage for one Design Model. Owns the scene, the HTML label
 *  layer and the hover tooltip; React owns the chrome around it. */
export class SceneController {
  constructor({ canvas, stage, labelLayer, model, onSelect, onState, onStats }) {
    this.model = model;
    this.canvas = canvas;
    this.stage = stage;
    this.labelLayer = labelLayer;
    this.onSelect = onSelect || (() => {});
    this.onState = onState || (() => {});
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.state = { labels: true, dims: false, xray: false, hover: null, sel: null, view: model.views?.[0]?.id };

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const st = model.stage || {};
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(css("--v3d-stage"));
    this.scene.fog = new THREE.Fog(this.scene.background.getHex(), st.fog_near ?? 70, st.fog_far ?? 150);
    this.camera = new THREE.PerspectiveCamera(st.fov ?? 32, 1, 0.1, 500);
    this.controls = new OrbitControls(this.camera, canvas);
    Object.assign(this.controls, {
      enableDamping: true, dampingFactor: 0.08, maxPolarAngle: 88 * DEG,
      minDistance: 4, maxDistance: 140, screenSpacePanning: true,
    });
    this.controls.addEventListener("start", () => {
      if (!this.tween && this.state.view) { this.state.view = null; this.onState(this.snapshot()); }
    });

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x80868d, 0.78));
    this.sun = new THREE.DirectionalLight(0xffffff, 0.72);
    this.sun.position.set(16, 28, 20);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0006;
    this.scene.add(this.sun);
    const fill = new THREE.DirectionalLight(0xffffff, 0.28);
    fill.position.set(-18, 10, -14);
    this.scene.add(fill);

    const built = buildModel(model);
    this.parts = built.parts;
    this.root = built.root;
    this.scene.add(this.root);
    (onStats || (() => {}))(built.stats);

    this.groundMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(css("--v3d-ground")), roughness: 1, metalness: 0 });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(st.ground_size ?? 220, st.ground_size ?? 220), this.groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);
    this.makeGrid();

    this.root.updateMatrixWorld(true);
    this.bbox = new THREE.Box3().setFromObject(this.root);
    this.fitShadowCamera();

    this.pickAll = built.meshes;
    this.buildLabels();
    this.buildDims();
    this.applyXray(false);

    this.ray = new THREE.Raycaster();
    this.ndc = new THREE.Vector2();
    this.W = 1; this.H = 1;
    this.bindPointer();

    this.resize = () => {
      this.W = this.stage.clientWidth || 1;
      this.H = this.stage.clientHeight || 1;
      this.renderer.setSize(this.W, this.H, false);
      this.camera.aspect = this.W / this.H;
      this.camera.updateProjectionMatrix();
    };
    this.resize();
    this.ro = new ResizeObserver(this.resize);
    this.ro.observe(this.stage);

    this.applyTheme = () => {
      const c = css("--v3d-stage");
      this.scene.background.set(c);
      this.scene.fog.color.set(c);
      this.groundMat.color.set(css("--v3d-ground"));
      this.dimMat.color.set(css("--v3d-dim"));
      this.makeGrid();
    };
    this.mq = window.matchMedia("(prefers-color-scheme: dark)");
    this.mq.addEventListener("change", this.applyTheme);
    this.themeObs = new MutationObserver(this.applyTheme);
    this.themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });

    this.goTo(this.state.view, true);
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }

  snapshot() {
    const { labels, dims, xray, sel, view } = this.state;
    return { labels, dims, xray, sel, view };
  }

  makeGrid() {
    const st = this.model.stage || {};
    if (this.grid) { this.scene.remove(this.grid); this.grid.geometry.dispose(); this.grid.material.dispose(); }
    const c = css("--v3d-grid");
    this.grid = new THREE.GridHelper(st.grid_size ?? 90, st.grid_divisions ?? 90, c, c);
    this.grid.position.y = 0.004;
    this.scene.add(this.grid);
  }

  fitShadowCamera() {
    const b = this.bbox, c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    const r = Math.max(s.x, s.z) * 0.75 + 2;
    Object.assign(this.sun.shadow.camera, { left: -r, right: r, top: r, bottom: -r, near: 1, far: 120 });
    this.sun.position.set(c.x + r * 0.8, b.max.y + r, c.z + r);
    this.sun.target.position.copy(c);
    this.scene.add(this.sun.target);
    this.sun.shadow.camera.updateProjectionMatrix();
  }

  // ---------- labels ----------
  addLabel(spec) {
    const el = document.createElement("div");
    el.className = "v3d-lbl" + (spec.cls ? " " + spec.cls : "");
    el.textContent = spec.text;
    el.dataset.off = "";
    el.setAttribute("data-testid", `v3d-label-${(spec.part || "dim")}-${this.labels.length}`);
    if (spec.part) el.addEventListener("click", (e) => { e.stopPropagation(); this.select(spec.part); });
    this.labelLayer.appendChild(el);
    this.labels.push({ ...spec, el, w: 0, h: 0 });
  }

  anchorPos(a) {
    const p = this.parts[a.part];
    const list = p?.anchors?.[a.name || "tip"] || [];
    const v = (list[a.instance || 0] || V3(0, 0, 0)).clone();
    const o = a.offset || [0, 0, 0];
    return v.add(V3(o[0], o[1], o[2]));
  }

  buildLabels() {
    this.labels = [];
    for (const l of this.model.labels || []) {
      const pos = l.anchor ? this.anchorPos(l.anchor) : V3(...(l.at || [0, 0, 0]));
      this.addLabel({ text: l.text, part: l.part, pos, priority: l.priority ?? 50, mode: l.mode || "normal" });
    }
  }

  buildDims() {
    this.dimMat = new THREE.LineBasicMaterial({ color: new THREE.Color(css("--v3d-dim")) });
    this.dimG = new THREE.Group();
    this.dimG.visible = false;
    this.scene.add(this.dimG);
    const b = this.bbox;
    const line = (a, c, t) => {
      const pts = [a, c, a.clone().addScaledVector(t, -0.3), a.clone().addScaledVector(t, 0.3),
        c.clone().addScaledVector(t, -0.3), c.clone().addScaledVector(t, 0.3)];
      this.dimG.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), this.dimMat));
    };
    for (const d of this.model.dimensions || []) {
      const pad = d.pad ?? 0.9;
      if (d.kind === "bbox_x") {
        const z = b.max.z + pad;
        line(V3(b.min.x, d.at_y ?? 0.45, z), V3(b.max.x, d.at_y ?? 0.45, z), V3(0, 0, 1));
        this.addLabel({ text: d.label.replace("{mm}", fmtMm(b.max.x - b.min.x)), pos: V3((b.min.x + b.max.x) / 2, d.at_y ?? 0.45, z), priority: d.priority ?? 50, mode: "dims", cls: "dimlbl" });
      } else if (d.kind === "bbox_z") {
        const x = b.max.x + pad;
        line(V3(x, d.at_y ?? 0.45, b.min.z), V3(x, d.at_y ?? 0.45, b.max.z), V3(1, 0, 0));
        this.addLabel({ text: d.label.replace("{mm}", fmtMm(b.max.z - b.min.z)), pos: V3(x, d.at_y ?? 0.45, 0), priority: d.priority ?? 50, mode: "dims", cls: "dimlbl" });
      } else if (d.kind === "height") {
        const x = b.min.x - pad, y0 = d.from_y ?? 0;
        line(V3(x, y0, 0), V3(x, b.max.y, 0), V3(1, 0, 0));
        this.addLabel({ text: d.label.replace("{mm}", fmtMm(b.max.y - y0)), pos: V3(x, (y0 + b.max.y) / 2, 0), priority: d.priority ?? 50, mode: "dims", cls: "dimlbl" });
      } else {
        this.addLabel({ text: d.label, pos: V3(...(d.at || [0, 0, 0])), priority: d.priority ?? 50, mode: "dims", cls: "dimlbl" });
      }
    }
    this.labelOrder = this.labels.slice().sort((a, c) => (c.mode === "dims") - (a.mode === "dims") || a.priority - c.priority);
    this.measureLabels();
    if (document.fonts?.ready) document.fonts.ready.then(() => this.measureLabels());
  }

  measureLabels() { this.labels.forEach((L) => { L.w = L.el.offsetWidth; L.h = L.el.offsetHeight; }); }

  placeLabels() {
    const placed = [];
    const v = new THREE.Vector3();
    for (const L of this.labelOrder || []) {
      let show = L.mode === "dims"
        ? this.state.dims
        : this.state.labels && (L.mode === "both" || (L.mode === "normal" && !this.state.xray) || (L.mode === "xray" && this.state.xray));
      if (show) {
        v.copy(L.pos).project(this.camera);
        if (v.z > 1 || Math.abs(v.x) > 1.02 || Math.abs(v.y) > 1.02) show = false;
        else {
          const x = (v.x * 0.5 + 0.5) * this.W, y = (-v.y * 0.5 + 0.5) * this.H, dims = L.mode === "dims";
          const bx = { l: x - L.w / 2 - 3, r: x + L.w / 2 + 3, t: y - L.h - (dims ? L.h / 2 : 9) - 2, b: y - (dims ? L.h / 2 : 9) + 2 };
          if (placed.some((p) => bx.l < p.r && bx.r > p.l && bx.t < p.b && bx.b > p.t)) show = false;
          else {
            placed.push(bx);
            L.el.style.transform = `translate(${Math.round(x - L.w / 2)}px,${Math.round(dims ? y - L.h / 2 : y - L.h - 9)}px)`;
          }
        }
      }
      if (show) L.el.removeAttribute("data-off"); else L.el.setAttribute("data-off", "");
    }
  }

  // ---------- selection / x-ray ----------
  paint() {
    for (const [k, p] of Object.entries(this.parts)) {
      const hex = k === this.state.sel ? 0x24456e : k === this.state.hover ? 0x16293f : 0x000000;
      p.mats.forEach((m) => m.emissive.setHex(hex));
    }
    this.labels.forEach((L) => L.el.classList.toggle("sel", !!L.part && L.part === this.state.sel));
  }

  applyXray(on) {
    const x = this.model.xray || {};
    const fade = x.fade || [], reveal = x.reveal || [];
    this.state.xray = on;
    for (const k of fade) {
      const p = this.parts[k];
      if (!p) continue;
      for (const m of p.mats) {
        m.transparent = on || m.userData.baseTransparent;
        m.opacity = on ? (x.opacity ?? 0.13) : m.userData.baseOpacity;
        m.depthWrite = !on;
        m.needsUpdate = true;
      }
    }
    for (const k of reveal) if (this.parts[k]) this.parts[k].group.visible = on;
    this.pickable = this.pickAll.filter((o) => {
      const part = o.userData.part;
      return on ? !fade.includes(part) : !reveal.includes(part);
    });
  }

  setXray(on) { this.applyXray(on); this.paint(); this.onState(this.snapshot()); }
  setLabels(on) { this.state.labels = on; this.onState(this.snapshot()); }
  setDims(on) { this.state.dims = on; this.dimG.visible = on; this.onState(this.snapshot()); }

  select(partId, fromList) {
    const next = partId && (partId !== this.state.sel || fromList) ? partId : null;
    this.state.sel = next;
    if (next && (this.model.xray?.reveal || []).includes(next) && !this.state.xray) this.applyXray(true);
    this.paint();
    this.onSelect(next);
    this.onState(this.snapshot());
  }

  // ---------- views ----------
  viewPose(id) {
    const v = (this.model.views || []).find((x) => x.id === id) || (this.model.views || [])[0];
    if (!v) return null;
    const a = this.camera.aspect || 1.5;
    const f = a < 1 ? 0.95 / Math.sqrt(a) : 1;
    const t = V3(...(v.target || [0, 0, 0]));
    return { pos: t.clone().addScaledVector(V3(...v.dir).normalize(), v.distance * f), target: t };
  }

  goTo(id, instant) {
    const p = this.viewPose(id);
    if (!p) return;
    this.state.view = id;
    this.onState(this.snapshot());
    if (instant || this.reduced) {
      this.camera.position.copy(p.pos);
      this.controls.target.copy(p.target);
      this.controls.update();
      this.tween = null;
      return;
    }
    this.tween = { fp: this.camera.position.clone(), ft: this.controls.target.clone(), tp: p.pos, tt: p.target, t0: performance.now(), dur: 750 };
  }

  // ---------- picking ----------
  bindPointer() {
    this.tip = document.createElement("div");
    this.tip.className = "v3d-tip";
    this.tip.style.display = "none";
    this.tip.setAttribute("data-testid", "v3d-tooltip");
    this.labelLayer.appendChild(this.tip);

    this._move = (e) => {
      const r = this.canvas.getBoundingClientRect();
      this.ptr = { x: e.clientX - r.left, y: e.clientY - r.top, type: e.pointerType };
    };
    this._leave = () => { this.ptr = null; this.state.hover = null; this.tip.style.display = "none"; this.paint(); };
    this._down = (e) => { this.down = { x: e.clientX, y: e.clientY }; };
    this._up = (e) => {
      if (!this.down) return;
      const moved = Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y);
      this.down = null;
      if (moved > 5) return; // drag, not a click
      const r = this.canvas.getBoundingClientRect();
      this.select(this.pickAt(e.clientX - r.left, e.clientY - r.top));
    };
    this.canvas.addEventListener("pointermove", this._move);
    this.canvas.addEventListener("pointerleave", this._leave);
    this.canvas.addEventListener("pointerdown", this._down);
    this.canvas.addEventListener("pointerup", this._up);
  }

  pickAt(x, y) {
    this.ndc.set((x / this.W) * 2 - 1, -(y / this.H) * 2 + 1);
    this.ray.setFromCamera(this.ndc, this.camera);
    const hit = this.ray.intersectObjects(this.pickable, false)[0];
    return hit ? hit.object.userData.part : null;
  }

  partMeta(id) { return (this.model.parts || []).find((p) => p.id === id); }

  loop(now) {
    this.raf = requestAnimationFrame(this.loop);
    if (this.tween) {
      const k = Math.min(1, (now - this.tween.t0) / this.tween.dur), e = ease(k);
      this.camera.position.lerpVectors(this.tween.fp, this.tween.tp, e);
      this.controls.target.lerpVectors(this.tween.ft, this.tween.tt, e);
      if (k === 1) this.tween = null;
    }
    this.controls.update();
    if (this.ptr && this.ptr.type !== "touch") {
      const k = this.pickAt(this.ptr.x, this.ptr.y);
      if (k !== this.state.hover) { this.state.hover = k; this.paint(); }
      if (k) {
        const m = this.partMeta(k) || {};
        this.tip.style.display = "block";
        this.tip.innerHTML = "";
        const b = document.createElement("b");
        b.textContent = m.name || k;
        this.tip.appendChild(b);
        this.tip.appendChild(document.createTextNode(m.tip || ""));
        const tx = Math.min(this.ptr.x, this.W - 270);
        this.tip.style.transform = `translate(${tx + 12}px,${this.ptr.y + 14}px)`;
      } else this.tip.style.display = "none";
      this.canvas.style.cursor = k ? "pointer" : "grab";
    }
    this.renderer.render(this.scene, this.camera);
    this.placeLabels();
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    this.themeObs?.disconnect();
    this.mq?.removeEventListener("change", this.applyTheme);
    this.canvas.removeEventListener("pointermove", this._move);
    this.canvas.removeEventListener("pointerleave", this._leave);
    this.canvas.removeEventListener("pointerdown", this._down);
    this.canvas.removeEventListener("pointerup", this._up);
    this.controls.dispose();
    this.scene.traverse((o) => {
      if (o.isMesh || o.isLine || o.isLineSegments) {
        o.geometry?.dispose();
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => m?.dispose());
      }
    });
    this.labelLayer.innerHTML = "";
    this.renderer.dispose();
  }
}
